import Button from '@orn/shared-web/components/Button/Button';
import Select, { type SelectItemType } from '@orn/shared-web/components/Select';
import { EditIcon, TrashIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { DashedAddButton } from '../common';

import { formatAccountAlertSummary } from './accountAlerts';

import type { IAvailableAccountAlert, IConfiguredAccountAlert } from './accountAlerts';

export interface IAccountAlertsSectionProps {
  /** Eligible accounts that already carry a per-account override. */
  configuredAccounts: IConfiguredAccountAlert[];
  /** Eligible accounts still inheriting the household default — the picker's options. */
  availableAccounts: IAvailableAccountAlert[];
  /** Open the account editor (the shared modal) for an existing or newly picked account. */
  onEditAccount: (account: { id: string; name: string }) => void;
  /** Clear an account's override back to the household default. */
  onRemoveAccount: (account: IConfiguredAccountAlert) => void;
  /** Accounts whose override is currently being removed (a per-row spinner each). */
  removingAccountIds?: ReadonlySet<string>;
}

const NO_REMOVING: ReadonlySet<string> = new Set();

const maskLabel = (name: string, mask: string | null): string => (mask ? `${name} ••${mask}` : name);

/**
 * The "account-specific alerts" subsection of the notifications form: lists the
 * accounts that override the household alert defaults (each editable via the
 * shared account modal, or removable back to the default), with an "add" picker
 * of the accounts still inheriting. Purely presentational — the wrapper owns the
 * modal, the remove mutation, and the eligible/configured partition.
 */
const AccountAlertsSection = ({
  configuredAccounts,
  availableAccounts,
  onEditAccount,
  onRemoveAccount,
  removingAccountIds = NO_REMOVING,
}: IAccountAlertsSectionProps) => {
  const [isAdding, setIsAdding] = React.useState(false);

  const pickerItems: SelectItemType[] = React.useMemo(
    () =>
      availableAccounts.map((a) => ({ id: a.id, label: a.name, supportingText: a.mask ? `••${a.mask}` : undefined })),
    [availableAccounts]
  );

  const handlePick = (key: string | number | null) => {
    if (key === null) {
      return;
    }
    const account = availableAccounts.find((a) => a.id === String(key));
    setIsAdding(false);
    if (account) {
      onEditAccount({ id: account.id, name: account.name });
    }
  };

  // Nothing to configure without a depository / credit account — hide the whole
  // subsection rather than show a dead heading.
  if (configuredAccounts.length === 0 && availableAccounts.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <div className="text-primary text-sm font-medium">Account-specific alerts</div>
        <div className="text-tertiary text-sm">
          Override the household defaults for a specific account, or turn its alerts off.
        </div>
      </div>

      {configuredAccounts.length > 0 && (
        <div className="flex flex-col gap-2">
          {configuredAccounts.map((account) => {
            const isRemoving = removingAccountIds.has(account.id);
            return (
              <div
                key={account.id}
                className="border-secondary flex items-center justify-between gap-3 rounded-xl border px-4 py-3"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="text-primary truncate text-sm font-medium">
                    {maskLabel(account.name, account.mask)}
                  </div>
                  <div className="text-tertiary text-sm">{formatAccountAlertSummary(account)}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    color="tertiary"
                    size="sm"
                    iconLeading={EditIcon}
                    aria-label={`Edit alerts for ${account.name}`}
                    onClick={() => onEditAccount({ id: account.id, name: account.name })}
                    isDisabled={isRemoving}
                  />
                  <Button
                    color="tertiary"
                    size="sm"
                    iconLeading={TrashIcon}
                    aria-label={`Remove alerts for ${account.name}`}
                    onClick={() => onRemoveAccount(account)}
                    isDisabled={isRemoving}
                    isLoading={isRemoving}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isAdding ? (
        <div className="flex items-start gap-2">
          <Select.ComboBox
            aria-label="Account"
            placeholder="Search accounts"
            shortcut={false}
            autoFocus
            items={pickerItems}
            value={null}
            onChange={handlePick}
            className="flex-1"
          >
            {(item) => (
              <Select.Item id={item.id} supportingText={item.supportingText}>
                {item.label}
              </Select.Item>
            )}
          </Select.ComboBox>
          <Button color="tertiary" size="md" onClick={() => setIsAdding(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        availableAccounts.length > 0 && (
          <DashedAddButton onClick={() => setIsAdding(true)}>Add account-specific alert</DashedAddButton>
        )
      )}
    </div>
  );
};

AccountAlertsSection.displayName = 'AccountAlertsSection';

export default AccountAlertsSection;
